import axiosInstance from "../axiosInstance";

export interface NotificationRecipient {
  userId: string;
  notificationType: "all_calls" | "flagged_only";
  isActive: boolean;
  userEmail?: string;
  userName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BulkUpdateItem {
  userId: string;
  notificationType: "all_calls" | "flagged_only";
  isActive: boolean;
}

export interface BulkUpdateRequest {
  updates: BulkUpdateItem[];
}

export interface BulkUpdateResponse {
  updated: number;
  failed: number;
  errors: Array<{
    userId: string;
    error: string;
  }>;
}

export interface NotificationStatistics {
  total: number;
  active: number;
  all_calls: number;
  flagged_only: number;
}

/**
 * API client for managing call scoring notification recipients and preferences
 */
export const notificationsApi = {
  /**
   * Retrieves the list of notification recipients with their preferences
   * @param activeOnly - If true, returns only active recipients
   * @returns Promise resolving to array of notification recipients
   */
  listRecipients: async (activeOnly = false): Promise<NotificationRecipient[]> => {
    const params = activeOnly ? { active_only: true } : {};
    const response = await axiosInstance.get(`/api/private/notifications/recipients`, { params });
    return response.data.data;
  },

  /**
   * Updates notification preferences for a specific user
   * @param userId - The unique identifier of the user
   * @param data - Updated notification preferences (type and active status)
   * @returns Promise resolving to the updated recipient data
   */
  updateRecipient: async (
    userId: string,
    data: Omit<NotificationRecipient, "userEmail" | "userName" | "createdAt" | "updatedAt">
  ): Promise<NotificationRecipient> => {
    const response = await axiosInstance.put(`/api/private/notifications/recipients/${userId}`, data);
    return response.data.data;
  },

  /**
   * Performs bulk updates on multiple notification recipients
   * @param updates - Array of updates to apply to multiple recipients
   * @returns Promise resolving to bulk update response with success/failure counts
   * @example
   * const updates = [
   *   { userId: "123", notificationType: "all_calls", isActive: true },
   *   { userId: "456", notificationType: "flagged_only", isActive: false }
   * ];
   * const result = await notificationsApi.bulkUpdateRecipients(updates);
   */
  bulkUpdateRecipients: async (updates: BulkUpdateItem[]): Promise<BulkUpdateResponse> => {
    const response = await axiosInstance.post(`/api/private/notifications/recipients/bulk`, { updates });
    return response.data.data;
  },

  /**
   * Retrieves aggregated statistics about notification recipients
   * @returns Promise resolving to notification statistics including counts by type
   */
  getStatistics: async (): Promise<NotificationStatistics> => {
    const response = await axiosInstance.get(`/api/private/notifications/recipients/statistics`);
    return response.data.data;
  },

  /**
   * Requests a test email preview showing who the email will be sent to.
   * Returns confirmation details including the logged-in user's email.
   */
  previewTestEmail: async (
    transcriptId: string,
  ): Promise<{
    requiresConfirmation: boolean;
    recipientEmail: string;
    recipientName: string;
    transcriptId: number;
    meetingTitle: string;
    score: string;
    message: string;
  }> => {
    const response = await axiosInstance.post(
      `/api/private/notifications/test-email/${transcriptId}`,
    );
    return response.data.data;
  },

  /**
   * Confirms and sends the test notification email to the logged-in user.
   */
  confirmTestEmail: async (
    transcriptId: string,
  ): Promise<{
    message: string;
    recipientEmail: string;
    transcriptId: number;
    score: string;
  }> => {
    const response = await axiosInstance.post(
      `/api/private/notifications/test-email/${transcriptId}`,
      { confirm: true },
    );
    return response.data.data;
  },
};